import React, { useState, useEffect, useMemo } from "react";

import {
  FileText,
  X,
  AlertTriangle,
  CheckCircle,
  Search,
  Loader2,
  Check,
  FolderOpen,
  RotateCcw,
  Folder,
} from "lucide-react";

import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import {
  adminGetFolders,
  adminGetFolderDocuments,
  adminUploadFolderInit,
  adminUploadFolderFinalize,
} from "../utils/Api";

async function uploadFilesInParallel(
  items: Array<{ url: string; file: File; contentType: string }>,
  concurrency: number,
  onFileAttempted: () => void,
): Promise<void> {
  let cursor = 0;
  const failures: string[] = [];

  async function worker() {
    while (cursor < items.length) {
      const myIndex = cursor++;
      const item = items[myIndex];
      if (!item) {
        onFileAttempted();
        continue;
      }
      try {
        const res = await fetch(item.url, {
          method: "PUT",
          headers: { "Content-Type": item.contentType },
          body: item.file,
        });
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          failures.push(`${item.file.name}: ${res.status} ${res.statusText}${body ? " — " + body.slice(0, 200) : ""}`);
          console.error("[upload] PUT failed", item.file.name, res.status, body);
        }
      } catch (err: any) {
        failures.push(`${item.file.name}: ${err?.message || "network error"}`);
        console.error("[upload] PUT error", item.file.name, err);
      } finally {
        onFileAttempted();
      }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()));

  if (failures.length > 0) {
    throw new Error(`Failed to upload ${failures.length} file(s): ${failures.slice(0, 3).join("; ")}`);
  }
}

const CHECKPOINT_KEY = "admin-upload-checkpoint";
const CHECKPOINT_TTL_MS = 24 * 60 * 60 * 1000;

interface UploadCheckpoint {
  batchId: string;
  targetType: "folder";
  targetId: string;
  completedDocStems: string[];
  savedAt: number;
}

function loadCheckpoint(): UploadCheckpoint | null {
  try {
    const raw = localStorage.getItem(CHECKPOINT_KEY);
    if (!raw) return null;
    const cp = JSON.parse(raw) as UploadCheckpoint;
    if (Date.now() - cp.savedAt > CHECKPOINT_TTL_MS) {
      localStorage.removeItem(CHECKPOINT_KEY);
      return null;
    }
    return cp;
  } catch {
    return null;
  }
}

function saveCheckpoint(cp: UploadCheckpoint) {
  localStorage.setItem(CHECKPOINT_KEY, JSON.stringify({ ...cp, savedAt: Date.now() }));
}

function clearCheckpoint() {
  localStorage.removeItem(CHECKPOINT_KEY);
}

const PAGE_PATTERN = /^(.+)_page_(\d+)$/i;
const IMAGE_EXTS = new Set([".png", ".jpg", ".jpeg", ".webp", ".tiff", ".tif"]);

interface PairedPage {
  stem: string;
  pageNum: number;
  pageFile: File;
  jsonFile: File;
  jsonData: any;
}

interface DocumentGroup {
  docStem: string;
  docId: string;
  pages: PairedPage[];
  isPassthrough?: boolean;
  passthroughPayload?: any;
  passthroughDocName?: string;
}

interface AdminFolder {
  id: string;
  name: string;
  doc_count: number;
}

interface AdminUploadProcessedModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Pre-select a folder when opened from a folder detail view */
  preselectedFolderId?: string | null;
}

const isRawPageJson = (json: any) =>
  typeof json.version === "string" &&
  typeof json.root === "string" &&
  json.objects != null &&
  typeof json.objects === "object";

const isDocumentPayload = (json: any) =>
  typeof json.document_id === "string" &&
  typeof json.document_name === "string" &&
  Array.isArray(json.pages);

const AdminUploadProcessedModal: React.FC<AdminUploadProcessedModalProps> = ({
  isOpen,
  setIsOpen,
  preselectedFolderId = null,
}) => {
  const [folders, setFolders] = useState<AdminFolder[]>([]);
  const [folderSearch, setFolderSearch] = useState("");
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<AdminFolder | null>(null);
  const [existingDocNames, setExistingDocNames] = useState<Set<string>>(new Set());

  const [documentGroups, setDocumentGroups] = useState<DocumentGroup[]>([]);
  const [skippedFiles, setSkippedFiles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [failedGroups, setFailedGroups] = useState<{ docStem: string; error: string }[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    setFolderSearch("");
    setSelectedFolder(null);
    setExistingDocNames(new Set());
    setDocumentGroups([]);
    setSkippedFiles([]);
    setError(null);
    setIsSubmitting(false);
    setProcessedCount(0);
    setFailedGroups([]);

    setFoldersLoading(true);
    adminGetFolders()
      .then((res) => {
        const data: AdminFolder[] = res.data ?? [];
        setFolders(data);
        if (preselectedFolderId) {
          const pre = data.find((f) => f.id === preselectedFolderId);
          if (pre) setSelectedFolder(pre);
        }
      })
      .catch(() => setFolders([]))
      .finally(() => setFoldersLoading(false));
  }, [isOpen, preselectedFolderId]);

  useEffect(() => {
    if (!isOpen || !selectedFolder) {
      setExistingDocNames(new Set());
      return;
    }
    let cancelled = false;
    adminGetFolderDocuments(selectedFolder.id, { limit: 10000 })
      .then((res) => {
        if (cancelled) return;
        const docs: { name: string }[] = res.data?.documents ?? [];
        setExistingDocNames(new Set(docs.map((d) => d.name.toLowerCase())));
      })
      .catch(() => {
        if (!cancelled) setExistingDocNames(new Set());
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedFolder]);

  const walkEntry = async (entry: any): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file((f: File) => resolve([f]), () => resolve([]));
      });
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const allEntries: any[] = [];
      const readBatch = (): Promise<void> =>
        new Promise((resolve, reject) => {
          reader.readEntries(async (entries: any[]) => {
            if (entries.length === 0) resolve();
            else {
              allEntries.push(...entries);
              await readBatch();
              resolve();
            }
          }, reject);
        });
      await readBatch();
      const nested = await Promise.all(allEntries.map(walkEntry));
      return nested.flat();
    }
    return [];
  };

  const processFiles = async (files: File[], folderName?: string | null) => {
    if (files.length === 0) return;
    setError(null);

    const stemMap = new Map<string, { pageFile?: File; json?: File }>();
    for (const file of files) {
      if (file.name.startsWith("._")) continue;
      const lastDot = file.name.lastIndexOf(".");
      if (lastDot === -1) continue;
      const ext = file.name.slice(lastDot).toLowerCase();
      const stem = file.name.slice(0, lastDot);
      if (ext !== ".pdf" && !IMAGE_EXTS.has(ext) && ext !== ".json") continue;
      const entry = stemMap.get(stem) ?? {};
      if (ext === ".json") entry.json = file;
      else entry.pageFile = file;
      stemMap.set(stem, entry);
    }

    const pairs: Array<{ stem: string; pageFile: File; jsonFile: File }> = [];
    const skipped: string[] = [];

    for (const [stem, { pageFile, json }] of stemMap) {
      if (pageFile && json) {
        pairs.push({ stem, pageFile, jsonFile: json });
      } else if (pageFile) {
        skipped.push(`${stem}${pageFile.name.slice(pageFile.name.lastIndexOf("."))} (no matching JSON)`);
      } else if (json) {
        skipped.push(`${stem}.json (no matching PDF or image)`);
      }
    }

    if (pairs.length === 0) {
      setError("No matching pairs found. Each JSON must have a PDF or image file with the same name.");
      setSkippedFiles(skipped);
      return;
    }

    const groupMap = new Map<string, DocumentGroup>();

    for (const { stem, pageFile, jsonFile } of pairs) {
      let jsonData: any;
      try {
        jsonData = JSON.parse(await jsonFile.text());
      } catch {
        setError(`'${jsonFile.name}' is not valid JSON.`);
        return;
      }

      if (isDocumentPayload(jsonData)) {
        const payloadPages = (jsonData.pages as any[]).slice().sort((a, b) => a.page_number - b.page_number);
        const pages: PairedPage[] = payloadPages.map((p: any, idx: number) => ({
          stem,
          pageNum: Number(p.page_number) || idx + 1,
          pageFile,
          jsonFile,
          jsonData: p.raw_data ?? {},
        }));
        groupMap.set(stem, {
          docStem: folderName ?? stem,
          docId: crypto.randomUUID(),
          pages,
          isPassthrough: true,
          passthroughPayload: jsonData,
          passthroughDocName: folderName ?? stem,
        });
        continue;
      }

      if (!isRawPageJson(jsonData)) {
        setError(`'${jsonFile.name}' is not a recognised pipeline JSON format (expected version, root, objects).`);
        return;
      }

      const match = stem.match(PAGE_PATTERN);
      const docStem = match ? match[1]! : stem;
      const pageNum = match ? parseInt(match[2]!, 10) : 1;

      const page: PairedPage = { stem, pageNum, pageFile, jsonFile, jsonData };

      const existing = groupMap.get(docStem);
      if (existing) {
        if (!existing.pages.some((p) => p.pageNum === pageNum)) {
          existing.pages.push(page);
        }
      } else {
        groupMap.set(docStem, { docStem, docId: crypto.randomUUID(), pages: [page] });
      }
    }

    setDocumentGroups((prev) => {
      const merged = prev.map((g) => ({ ...g, pages: [...g.pages] }));
      for (const newGroup of groupMap.values()) {
        const existing = merged.find((g) => g.docStem === newGroup.docStem);
        if (existing) {
          for (const page of newGroup.pages) {
            if (!existing.pages.some((p) => p.pageNum === page.pageNum)) {
              existing.pages.push(page);
            }
          }
        } else {
          merged.push(newGroup);
        }
      }
      return merged;
    });
    setSkippedFiles((prev) => [...prev, ...skipped]);
  };

  const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const folderName = files[0]?.webkitRelativePath?.split("/")[0] ?? null;
    await processFiles(files, folderName);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!selectedFolder) return;

    const items = Array.from(e.dataTransfer.items || []);
    const entries = items
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => entry != null);

    if (entries.length === 0) {
      await processFiles(Array.from(e.dataTransfer.files || []));
      return;
    }

    for (const entry of entries) {
      const files = await walkEntry(entry);
      const folderName = entry.isDirectory ? entry.name : null;
      await processFiles(files, folderName);
    }
  };

  const alreadyUploadedStems = useMemo<Set<string>>(() => {
    if (!selectedFolder) return new Set();
    const cp = loadCheckpoint();
    if (!cp || cp.targetId !== selectedFolder.id) return new Set();
    return new Set(cp.completedDocStems);
  }, [selectedFolder, documentGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateStems = useMemo<Set<string>>(() => {
    if (existingDocNames.size === 0) return new Set();
    const dups = new Set<string>();
    for (const g of documentGroups) {
      if (existingDocNames.has(g.docStem.toLowerCase())) dups.add(g.docStem);
    }
    return dups;
  }, [documentGroups, existingDocNames]);

  const pendingGroups = documentGroups.filter(
    (g) => !alreadyUploadedStems.has(g.docStem) && !duplicateStems.has(g.docStem),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolder || pendingGroups.length === 0) return;

    setIsSubmitting(true);
    setProcessedCount(0);
    setError(null);
    setFailedGroups([]);

    const existing = loadCheckpoint();
    const checkpoint: UploadCheckpoint =
      existing && existing.targetId === selectedFolder.id
        ? existing
        : { batchId: crypto.randomUUID(), targetType: "folder", targetId: selectedFolder.id, completedDocStems: [], savedAt: 0 };

    let uploadedPages = 0;
    const failures: { docStem: string; error: string }[] = [];

    for (const group of pendingGroups) {
      // Build the init payload (small JSON only) and a parallel list of
      // (page_id, file) tuples so we can PUT each file to its signed URL.
      const pageEntries: Array<{
        page_id: string;
        page_number: number;
        raw_data: unknown;
        ext: string | null;
        file: File | null;
      }> = [];

      if (group.isPassthrough) {
        const docName = group.passthroughDocName!;
        const passthroughPages = group.passthroughPayload.pages as any[];
        passthroughPages.forEach((p: any, idx: number) => {
          const paired = group.pages[idx];
          const ext = paired ? paired.pageFile.name.slice(paired.pageFile.name.lastIndexOf(".") + 1).toLowerCase() : null;
          pageEntries.push({
            page_id: crypto.randomUUID(),
            page_number: p.page_number ?? idx + 1,
            raw_data: p.raw_data ?? p,
            ext: ext || null,
            file: paired ? paired.pageFile : null,
          });
        });
        // Suppress unused-warning for docName
        void docName;
      } else {
        [...group.pages]
          .sort((a, b) => a.pageNum - b.pageNum)
          .forEach((p) => {
            const ext = p.pageFile.name.slice(p.pageFile.name.lastIndexOf(".") + 1).toLowerCase();
            pageEntries.push({
              page_id: p.jsonData.root,
              page_number: p.pageNum,
              raw_data: p.jsonData,
              ext: ext || null,
              file: p.pageFile,
            });
          });
      }

      const isLast = group === pendingGroups[pendingGroups.length - 1];

      try {
        // 1. Init — create DB rows, get signed URLs
        const initResp: any = await adminUploadFolderInit("folder", selectedFolder.id, [
          {
            document_id: group.docId,
            document_name: group.docStem,
            pages: pageEntries.map(({ page_id, page_number, raw_data, ext }) => ({
              page_id,
              page_number,
              raw_data,
              ext,
            })),
          },
        ]);

        const respDoc = initResp.data.documents[0];
        const fileByPageId = new Map(pageEntries.map((p) => [p.page_id, p.file]));

        // 2. PUT each file to its signed URL in parallel
        const uploadItems = respDoc.pages
          .filter((p: any) => p.upload_url && fileByPageId.get(p.page_id))
          .map((p: any) => ({
            url: p.upload_url as string,
            file: fileByPageId.get(p.page_id) as File,
            contentType: p.content_type as string,
          }));

        await uploadFilesInParallel(uploadItems, 8, () => {
          uploadedPages += 1;
          setProcessedCount(uploadedPages);
        });

        // 3. Finalize — enqueue Cloud Tasks
        await adminUploadFolderFinalize([group.docId], {
          notify: isLast,
          targetType: "folder",
          targetId: selectedFolder.id,
        });

        checkpoint.completedDocStems.push(group.docStem);
        saveCheckpoint(checkpoint);
      } catch (err: any) {
        failures.push({ docStem: group.docStem, error: err?.response?.data?.detail || err?.message || "Upload failed" });
        setFailedGroups([...failures]);
      }
    }

    setIsSubmitting(false);

    if (failures.length === 0) {
      clearCheckpoint();
      setIsOpen(false);
    }
  };

  const filteredFolders = folders.filter((f) =>
    f.name.toLowerCase().includes(folderSearch.toLowerCase()),
  );

  const fileInputId = "admin-upload-file-input";

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <h1 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
        Upload documents
      </h1>
      <p className="text-sm text-gray-500 mb-5">
        Select a staging folder, then drop your paired PDF/image and JSON files.
      </p>

      {/* Folder picker — hidden when already inside a folder */}
      {!isSubmitting && !preselectedFolderId && (
        <div className="space-y-2 mb-4">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400">Staging folder</p>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search folders…"
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
            />
          </div>

          {foldersLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="text-gray-400 animate-spin" />
            </div>
          ) : filteredFolders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
              <Folder size={32} />
              <p className="text-sm">{folders.length === 0 ? "No staging folders yet. Create one on the Documents page." : "No folders match your search."}</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
              {filteredFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => setSelectedFolder(selectedFolder?.id === folder.id ? null : folder)}
                  className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border ${
                    selectedFolder?.id === folder.id
                      ? "bg-[#1d365d]/5 border-[#1d365d]/30 text-[#1d365d]"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Folder size={16} className={selectedFolder?.id === folder.id ? "text-[#1d365d] flex-shrink-0" : "text-gray-400 flex-shrink-0"} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{folder.name}</p>
                      <p className="text-xs text-gray-400">{folder.doc_count} document{folder.doc_count !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  {selectedFolder?.id === folder.id && (
                    <div className="w-5 h-5 rounded-full bg-[#1d365d] flex items-center justify-center flex-shrink-0">
                      <Check size={11} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="border-t border-gray-100 pt-4 space-y-3">
          {!isSubmitting && (
            <div
              onDragEnter={(e) => { if (!selectedFolder) return; e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
              onDragOver={(e) => { if (!selectedFolder) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget as Node)) return; setIsDragging(false); }}
              onDrop={handleDrop}
              className={`border border-dashed rounded-lg p-5 text-center transition-colors ${
                !selectedFolder
                  ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                  : isDragging
                    ? "border-[#1d365d] bg-[#1d365d]/5"
                    : "border-gray-200 bg-gray-50/50 hover:border-[#1d365d]/40"
              }`}
            >
              <FolderOpen
                className={`mx-auto mb-2 ${selectedFolder ? "text-[#1d365d]" : "text-gray-300"}`}
                size={32}
              />
              {selectedFolder ? (
                <>
                  <label
                    htmlFor={fileInputId}
                    className="text-sm font-medium text-[#1d365d] cursor-pointer hover:text-[#2d4a75] transition-colors"
                  >
                    {documentGroups.length > 0 ? "Add another folder" : "Choose folder"}
                  </label>
                  <input
                    id={fileInputId}
                    type="file"
                    // @ts-ignore
                    webkitdirectory=""
                    multiple
                    className="sr-only"
                    onChange={handleFolderChange}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {isDragging ? "Drop folders to add" : "Click to pick one folder, or drag & drop multiple folders here"}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400">Select a staging folder above to enable upload</p>
              )}
            </div>
          )}

          {alreadyUploadedStems.size > 0 && !isSubmitting && (
            <div className="flex items-start justify-between gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={13} className="flex-shrink-0 mt-0.5" />
                <span>
                  <span className="font-medium">{alreadyUploadedStems.size} document{alreadyUploadedStems.size !== 1 ? "s" : ""}</span> already uploaded — {pendingGroups.length} remaining.
                </span>
              </div>
              <button type="button" title="Clear resume data and re-upload everything" onClick={clearCheckpoint} className="flex items-center gap-1 text-blue-500 hover:text-blue-700 transition-colors flex-shrink-0 font-medium">
                <RotateCcw size={11} />
                Reset
              </button>
            </div>
          )}

          {duplicateStems.size > 0 && !isSubmitting && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-medium">{duplicateStems.size} document{duplicateStems.size !== 1 ? "s" : ""}</span> already exist in <span className="font-medium">{selectedFolder?.name}</span> and will be skipped.
              </span>
            </div>
          )}

          {documentGroups.length > 0 && !isSubmitting && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
                {documentGroups.length} document{documentGroups.length !== 1 ? "s" : ""}
                {alreadyUploadedStems.size > 0 || duplicateStems.size > 0
                  ? ` (${pendingGroups.length} pending)`
                  : " to upload"}
              </p>
              {documentGroups.map((group) => {
                const done = alreadyUploadedStems.has(group.docStem);
                const duplicate = duplicateStems.has(group.docStem);
                const failed = failedGroups.find((f) => f.docStem === group.docStem);
                return (
                  <div
                    key={group.docStem}
                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${
                      failed
                        ? "border-red-200 bg-red-50"
                        : done
                          ? "border-gray-100 bg-gray-50 opacity-60"
                          : duplicate
                            ? "border-amber-200 bg-amber-50"
                            : "border-gray-200"
                    }`}
                  >
                    {failed ? (
                      <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                    ) : done ? (
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    ) : duplicate ? (
                      <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                    ) : (
                      <FileText size={14} className="text-[#1d365d] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${failed ? "text-red-700" : done ? "text-gray-400 line-through" : duplicate ? "text-amber-800" : "text-gray-900"}`}>
                        {group.docStem}
                      </p>
                      <p className={`text-xs truncate ${failed ? "text-red-500" : duplicate ? "text-amber-600" : "text-gray-400"}`}>
                        {failed
                          ? failed.error
                          : done
                            ? "already uploaded"
                            : duplicate
                              ? "already exists in this folder — will be skipped"
                              : `${group.pages.length} page${group.pages.length !== 1 ? "s" : ""} (${group.pages.map((p) => p.pageNum).sort((a, b) => a - b).join(", ")})`}
                      </p>
                    </div>
                    {!done && (
                      <button
                        type="button"
                        onClick={() => setDocumentGroups((prev) => prev.filter((g) => g.docStem !== group.docStem))}
                        className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {skippedFiles.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <p className="font-medium mb-1">{skippedFiles.length} file{skippedFiles.length !== 1 ? "s" : ""} skipped (no matching pair):</p>
              <ul className="space-y-0.5">
                {skippedFiles.map((f) => <li key={f} className="truncate">{f}</li>)}
              </ul>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {failedGroups.length > 0 && !isSubmitting && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-medium">{failedGroups.length} document{failedGroups.length !== 1 ? "s" : ""} failed.</span>{" "}
                Fix any issues and click Upload to retry.
              </span>
            </div>
          )}

          {isSubmitting && (
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 size={28} className="text-[#1d365d] animate-spin" />
              <p className="text-sm font-medium text-gray-700">
                {processedCount}/{pendingGroups.reduce((sum, g) => sum + g.pages.length, 0)}
              </p>
              <p className="text-xs text-gray-400">Uploading documents…</p>
            </div>
          )}

          <PrimaryButton
            type="submit"
            disabled={!selectedFolder || pendingGroups.length === 0 || isSubmitting}
            className="flex items-center justify-center gap-2 w-full"
          >
            {(() => {
              const total = pendingGroups.length;
              if (failedGroups.length > 0 && total === failedGroups.length)
                return `Retry ${total} failed document${total !== 1 ? "s" : ""}`;
              return `Upload ${total > 0 ? `${total} document${total !== 1 ? "s" : ""}` : "documents"}`;
            })()}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
};

export default AdminUploadProcessedModal;
